import { isMeterAccessibility } from '@enmon/commons/lib/enmon/meters/accessibility';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import {
  readInventorizationTechnicalDetails,
  readMountType,
  readOutputTechnology,
} from '@/features/inventorization/domain/inventorization-technical';
import type { CaptureSession } from '@/shared/capture/capture-session';
import type { MeterSearchResult } from '@/shared/meter-identification';

export function createTechnicalPatchFromMeter(
  session: CaptureSession,
  meter: MeterSearchResult,
): Partial<InventorizationTechnicalDetails> {
  const currentTechnical = readInventorizationTechnicalDetails(session);
  const detail = {
    ...currentTechnical.detail,
    ...serializeDetailRecord(meter.data.detail),
    ...compactRecord({
      internalSupplyPointCode: meter.data.internalSupplyPointCode,
      lorawanRssi: readOptionalString(meter.data.lorawanRssi),
      nbiotRssi: readOptionalString(meter.data.nbiotRssi),
      ownerType: meter.data.ownerType,
      requiresManualAks: readOptionalString(meter.data.requiresManualAks),
      submittedBy: meter.data.submittedBy,
    }),
  };
  const patch: Partial<InventorizationTechnicalDetails> = {
    detail,
  };
  const mountType = readMountType(meter.data.mountType);

  if (meter.data.name || meter.data.serial) {
    patch.monitoredEntity = meter.data.name ?? meter.data.serial ?? '';
  }

  const mountTechnology = readOutputTechnology(meter.data.mountTechnology);

  if (mountTechnology) {
    patch.mountTechnology = mountTechnology;
  }

  if (mountType) {
    patch.mountType = mountType;
  }

  if (meter.data.remoteReadingSerial) {
    patch.remoteReadingSerial = meter.data.remoteReadingSerial;
  }

  if (meter.data.remoteReadingOwner) {
    patch.remoteReadingOwner = meter.data.remoteReadingOwner;
  }

  const accessibility = toMeterAccessibilityArray(meter.data.accessibility);

  if (accessibility) {
    patch.accessibility = accessibility;
  }

  return patch;
}

function readOptionalString(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toString();
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function toMeterAccessibilityArray(value: unknown) {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.filter(isMeterAccessibility);
}

function compactRecord(values: Record<string, string | null | undefined>) {
  return Object.fromEntries(Object.entries(values).filter((entry): entry is [string, string] => Boolean(entry[1])));
}

function serializeDetailRecord(detail: Record<string, unknown> | undefined) {
  if (!detail) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(detail).flatMap(([key, value]) => {
      const serialized = readOptionalString(value);

      return serialized ? [[key, serialized]] : [];
    }),
  );
}
